/**
 * ErrorBoundary — a crashing page must never leave a white screen.
 *  • Stale-deploy chunk errors (the old index.html asks for a JS file that no longer exists) are
 *    healed automatically with ONE hard reload.
 *  • Anything else shows a friendly message with Retry / Reload / Home. Navigating elsewhere resets it.
 */
import { Component } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, RotateCcw } from "lucide-react";

const CHUNK_RE = /Loading chunk|ChunkLoadError|dynamically imported module|Importing a module script failed|error loading dynamically/i;
const FLAG = "app.chunk-reload-at";
export const isChunkError = (e) => CHUNK_RE.test(`${e?.name || ""} ${e?.message || ""}`);

/** Reload once (per 60 s) to pick up a fresh deploy; returns true when a reload was started. */
export async function reloadForNewDeploy() {
  try {
    const last = Number(sessionStorage.getItem(FLAG) || 0);
    if (Date.now() - last < 60000) return false;
    sessionStorage.setItem(FLAG, String(Date.now()));
  } catch { /* storage unavailable: still allow one attempt */ }
  try { const regs = await navigator.serviceWorker?.getRegistrations?.(); await Promise.all((regs || []).map((r) => r.update().catch(() => {}))); } catch { /* ignore */ }
  try { const keys = await caches?.keys?.(); await Promise.all((keys || []).map((k) => caches.delete(k))); } catch { /* ignore */ }
  window.location.reload();
  return true;
}

/** React.lazy that survives a stale deploy: on a chunk error, reload once. */
export const lazyRetry = (lazy) => (factory) => lazy(() => factory().catch(async (e) => {
  if (isChunkError(e) && await reloadForNewDeploy()) return new Promise(() => {}); // page is reloading
  throw e;
}));

class Boundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    console.error("Page crashed:", error, info?.componentStack);
    if (isChunkError(error)) reloadForNewDeploy();
  }
  componentDidUpdate(prev) { if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null }); }
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const chunk = isChunkError(error);
    return (
      <div role="alert" className="min-h-[50vh] flex items-center justify-center px-4 py-16 text-center">
        <div className="max-w-md">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
          <h1 className="text-lg font-bold">{chunk ? "A new version is available" : "This page hit a problem"}</h1>
          <p className="text-sm text-muted-foreground mt-2">{chunk ? "Reloading will fetch the latest version." : "Nothing you did was lost. Try again, or go back to the home page."}</p>
          {!chunk && <p className="mt-3 text-[11px] font-mono text-muted-foreground/80 break-words">{String(error?.message || error).slice(0, 200)}</p>}
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            <button onClick={() => (chunk ? window.location.reload() : this.setState({ error: null }))} className="inline-flex items-center gap-1.5 rounded-xl bg-primary text-primary-foreground px-4 py-2 text-sm font-medium"><RotateCcw className="w-4 h-4" />{chunk ? "Reload" : "Try again"}</button>
            <a href={this.props.home || "/"} className="inline-flex items-center rounded-xl border border-border px-4 py-2 text-sm font-medium hover:bg-muted">Go to home</a>
          </div>
        </div>
      </div>
    );
  }
}

export default function ErrorBoundary({ children, home }) {
  const { pathname } = useLocation();
  return <Boundary resetKey={pathname} home={home}>{children}</Boundary>;
}
